export type AdjustmentUnit = "flat" | "percent";

export interface AdjustmentAmounts {
  adjustmentFlat: string;
  adjustmentPercent: string;
  perCurrencyAmountsEnabled: boolean;
}

const ZERO = "0";

/**
 * An Adjustment has no unit discriminant and the calculator sums `flat` and
 * `proportion`, so switching units must zero the unit being left.
 */
export function adjustmentAmountsForUnit({
  displayedValue,
  toUnit,
  perCurrencyAmountsEnabled = false,
}: {
  displayedValue: string;
  toUnit: AdjustmentUnit;
  perCurrencyAmountsEnabled?: boolean;
}): AdjustmentAmounts {
  if (toUnit === "flat") {
    return {
      adjustmentFlat: displayedValue,
      adjustmentPercent: ZERO,
      perCurrencyAmountsEnabled,
    };
  }
  // Per-currency amounts replace `flat` outright, so they keep a flat fee alive
  // in percent mode. Disabled rather than cleared to survive a round trip.
  return {
    adjustmentFlat: ZERO,
    adjustmentPercent: displayedValue,
    perCurrencyAmountsEnabled: false,
  };
}

/** True when an adjustment charges in both units at once. */
export function hasConflictingAdjustmentUnits({
  adjustmentFlat,
  adjustmentPercent,
}: {
  adjustmentFlat: string | undefined;
  adjustmentPercent: string | undefined;
}): boolean {
  return isNonZeroAmount(adjustmentFlat) && isNonZeroAmount(adjustmentPercent);
}

/** Keeps the minus sign ahead of the currency symbol, as `-$3.50`. */
export function formatFlatAmount(adjustmentFlat: string | undefined): string {
  const amount = +(adjustmentFlat ?? 0);
  return `${amount < 0 ? "-" : ""}$${Math.abs(amount)}`;
}

export function isNonZeroAmount(value: string | undefined): boolean {
  if (value === undefined) {
    return false;
  }
  const parsed = parseFloat(value);
  return !isNaN(parsed) && parsed !== 0;
}
